import { Card, Table, Typography } from 'antd';
import { useState } from 'react';
import { PeriodComparisonToggle } from './PeriodComparisonToggle';
import { useIncomeStatement } from './hooks/useIncomeStatement';
import { fmtCents } from './shared/formatCents';
import { defaultBudgetItemsConfig } from '../../../../../../types/apiTypes';
import { unicodeToEmoji } from '../../../../../../shared/emojiUnicode';

const CATEGORY_LABELS: Record<string, string> = Object.fromEntries(
  Object.values(defaultBudgetItemsConfig).map((item) => [
    item.category,
    `${unicodeToEmoji(item.emoji_unicode)} ${item.category_display_name}`,
  ])
);

const formatExpenseLabel = (category: string): string =>
  CATEGORY_LABELS[category] ?? category.replace(/_/g, ' ');

export function IncomeStatementCard({
  customerId,
  year,
}: {
  customerId: string;
  year: number;
}) {
  const [compare, setCompare] = useState(false);
  const { data, isLoading } = useIncomeStatement(
    customerId,
    year,
    compare ? 'previous' : undefined
  );

  if (isLoading || !data) {
    return <Card title="Income Statement" loading />;
  }

  type Row = {
    key: string;
    label: string;
    current: number;
    previous?: number;
    delta?: number;
    expenseRow?: boolean;
    emphasis?: boolean;
  };

  const rows: Row[] = [
    {
      key: 'revenue',
      label: 'Revenue',
      current: data.gross_revenue,
      previous: data.previous?.gross_revenue,
      delta: data.delta?.gross_revenue_amount,
    },
    ...Object.entries(data.by_category as Record<string, number>).map(
      ([cat, amt]) => ({
        key: `cat-${cat}`,
        label: formatExpenseLabel(cat),
        current: amt as number,
        previous: data.previous?.by_category?.[cat],
        delta: data.previous
          ? (amt as number) - (data.previous.by_category?.[cat] ?? 0)
          : undefined,
        expenseRow: true,
      })
    ),
    {
      key: 'total-expenses',
      label: 'Total Expenses',
      current: data.total_expenses,
      previous: data.previous?.total_expenses,
      delta: data.delta?.total_expenses_amount,
      expenseRow: true,
      emphasis: true,
    },
    {
      key: 'net-profit',
      label: 'Net Profit',
      current: data.net_profit,
      previous: data.previous?.net_profit,
      delta: data.delta?.net_profit_amount,
      emphasis: true,
    },
  ];

  if (data.uncategorized_count > 0) {
    rows.push({
      key: 'uncategorized',
      label: `Uncategorized (${data.uncategorized_count})`,
      current: data.uncategorized_total,
      expenseRow: true,
    });
  }

  // Delta color: revenue/profit green when up, expenses green when down
  const deltaColor = (row: Row) => {
    if (row.delta == null || row.delta === 0) return undefined;
    const wantIncrease = !row.expenseRow;
    const goodDirection = wantIncrease ? row.delta > 0 : row.delta < 0;
    return goodDirection ? '#3f8600' : '#cf1322';
  };

  // Render a dollar amount, prefixed with `-` when it sits on an expense row.
  const renderAmount = (v: number | undefined, r: Row) => {
    if (v == null) return '—';
    return r.expenseRow ? `-${fmtCents(v)}` : fmtCents(v);
  };

  // Render the period-over-period delta. For expense rows, invert the sign so
  // the displayed value reflects impact on profit: an expense INCREASE shows as
  // negative, an expense DECREASE shows as positive. Color still reflects
  // whether the change is good (green) or bad (red) per `deltaColor`.
  const renderDelta = (v: number | undefined, r: Row) => {
    if (v == null) return '—';
    const display = r.expenseRow ? -v : v;
    const sign = display >= 0 ? '+' : '-';
    return (
      <span style={{ color: deltaColor(r) }}>
        {sign}
        {fmtCents(Math.abs(display))}
      </span>
    );
  };

  const columns: any[] = [
    {
      title: 'Category',
      dataIndex: 'label',
      width: 220,
      render: (v: string, r: Row) => (
        <Typography.Text strong={r.emphasis}>{v}</Typography.Text>
      ),
    },
    {
      title: `${year}`,
      dataIndex: 'current',
      align: 'right' as const,
      width: 130,
      render: (v: number, r: Row) => (
        <Typography.Text strong={r.emphasis}>
          {renderAmount(v, r)}
        </Typography.Text>
      ),
    },
  ];

  if (compare) {
    columns.push(
      {
        title: `${year - 1}`,
        dataIndex: 'previous',
        align: 'right' as const,
        width: 130,
        render: (v: number | undefined, r: Row) => renderAmount(v, r),
      },
      {
        title: 'Δ',
        dataIndex: 'delta',
        align: 'right' as const,
        width: 130,
        render: (v: number | undefined, r: Row) => renderDelta(v, r),
      }
    );
  }

  return (
    <Card>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="m-0 text-base font-semibold">Income Statement</h3>
        <PeriodComparisonToggle value={compare} onChange={setCompare} />
      </div>
      <Table
        dataSource={rows}
        columns={columns}
        pagination={false}
        size="small"
        scroll={{ x: 'max-content' }}
      />
    </Card>
  );
}
